import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BundleSummary } from '../lib/catalogTypes';

const API_URL = import.meta.env.VITE_API_URL as string;

export function imgSrc(path: string | null | undefined) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  // Paths already containing /storage/ are complete; others (e.g. /bundles/...) need it inserted.
  if (path.startsWith('/storage/')) return `${API_URL}${path}`;
  return `${API_URL}/storage${path}`;
}

const TYPE_LABELS: Record<string, string> = {
  fixed:       'Fixed Bundle',
  byob:        'Build Your Own',
  mix_match:   'Mix & Match',
  frame_print: 'Frame + Print',
};

export function discountLabel(
  b: Pick<BundleSummary, 'discount_type' | 'discount_value' | 'buy_quantity' | 'get_quantity'>,
): string {
  switch (b.discount_type) {
    case 'percentage':   return `Save ${b.discount_value}%`;
    case 'fixed_amount': return `Save EGP ${b.discount_value}`;
    case 'fixed_price':  return `EGP ${b.discount_value}`;
    case 'buy_x_get_y':  return `Buy ${b.buy_quantity} Get ${b.get_quantity} Free`;
    default: return '';
  }
}

function isEndingSoon(endsAt: string | null): boolean {
  if (!endsAt) return false;
  return new Date(endsAt).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000;
}

export function BundleCard({ bundle }: { bundle: BundleSummary }) {
  const image   = imgSrc(bundle.image);
  const endSoon = isEndingSoon(bundle.ends_at);
  const label   = discountLabel(bundle);

  // Ripple on CTA
  const btnRef  = useRef<HTMLSpanElement>(null);
  const [ripple, setRipple] = useState<{ x: number; y: number } | null>(null);

  function handleRipple(e: React.MouseEvent) {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    setRipple({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setTimeout(() => setRipple(null), 600);
  }

  return (
    <Link
      to={`/bundles/${bundle.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl bg-white outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
      style={{
        boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
        (e.currentTarget as HTMLElement).style.boxShadow = '0 20px 60px rgba(0,0,0,0.13)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.transform = '';
        (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 16px rgba(0,0,0,0.07)';
      }}
      aria-label={`${bundle.title} — ${label}`}
    >
      {/* ── Image ──────────────────────────────────────────────── */}
      <div className="relative aspect-[4/3] overflow-hidden bg-[#F2EDE5]">
        {image ? (
          <img
            src={image}
            alt={bundle.title}
            loading="lazy"
            className="h-full w-full object-cover"
            style={{ transition: 'transform 0.7s ease' }}
            onMouseEnter={e => ((e.currentTarget as HTMLImageElement).style.transform = 'scale(1.05)')}
            onMouseLeave={e => ((e.currentTarget as HTMLImageElement).style.transform = '')}
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-gradient-to-br from-[#F2EDE5] to-[#E5DDD0]">
            <svg className="h-16 w-16 text-[#C8BFB2]" fill="none" viewBox="0 0 64 64" aria-hidden="true">
              <rect x="8" y="8" width="22" height="22" rx="3" stroke="currentColor" strokeWidth="1.5" />
              <rect x="34" y="8" width="22" height="22" rx="3" stroke="currentColor" strokeWidth="1.5" />
              <rect x="8" y="34" width="22" height="22" rx="3" stroke="currentColor" strokeWidth="1.5" />
              <rect x="34" y="34" width="22" height="22" rx="3" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </div>
        )}

        {/* Bottom gradient for legibility */}
        <div
          className="absolute inset-x-0 bottom-0 h-28"
          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)' }}
        />

        {/* Top-left: badge_text */}
        {bundle.badge_text && (
          <span
            className="absolute left-3 top-3 z-10 rounded-full px-3 py-1 text-[11px] font-semibold text-white shadow-sm"
            style={{ background: '#C9A84C' }}
          >
            {bundle.badge_text}
          </span>
        )}

        {/* Top-right: featured ribbon or ribbon_text */}
        {(bundle.is_featured || bundle.ribbon_text) && (
          <span
            className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold text-white shadow-sm"
            style={{ background: bundle.is_featured ? '#C9A84C' : '#1A1612' }}
            aria-label={bundle.is_featured ? 'Featured bundle' : bundle.ribbon_text ?? ''}
          >
            {bundle.is_featured && <span aria-hidden="true">★</span>}
            {bundle.ribbon_text ?? 'Featured'}
          </span>
        )}

        {/* Bottom-left: item count */}
        {(bundle.products_count ?? 0) > 0 && (
          <span className="absolute bottom-3 left-3 z-10 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-medium text-ink backdrop-blur-sm">
            {bundle.products_count} {bundle.products_count === 1 ? 'item' : 'items'}
          </span>
        )}

        {/* Bottom-right: discount badge */}
        {label && (
          <span
            className="absolute bottom-3 right-3 z-10 rounded-full px-3 py-1 text-[11px] font-bold text-white shadow"
            style={{ background: '#C9A84C' }}
          >
            {label}
          </span>
        )}

        {/* Ending soon ribbon */}
        {endSoon && (
          <div className="absolute left-0 right-0 top-0 z-10 flex justify-center pt-2">
            <span className="rounded-full bg-red-500/90 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow">
              ⏰ Ending Soon
            </span>
          </div>
        )}
      </div>

      {/* ── Body ───────────────────────────────────────────────── */}
      <div className="flex flex-1 flex-col p-5">
        {/* Type label */}
        {'type' in bundle && (bundle as BundleSummary & { type?: string }).type && (
          <span className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em] text-stone">
            {TYPE_LABELS[(bundle as BundleSummary & { type?: string }).type ?? ''] ?? ''}
          </span>
        )}

        <h3 className="font-serif text-[17px] font-light leading-snug text-ink line-clamp-2">
          {bundle.title}
        </h3>

        {bundle.description && (
          <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-stone">
            {bundle.description}
          </p>
        )}

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between border-t border-[#F0ECE6] pt-4">
          <div>
            {endSoon && bundle.ends_at && (
              <p className="text-[11px] font-medium text-red-500">
                Ends {new Date(bundle.ends_at).toLocaleDateString('en-EG', { month: 'short', day: 'numeric' })}
              </p>
            )}
            {!endSoon && label && (
              <p className="text-sm font-semibold" style={{ color: '#C9A84C' }}>
                {label}
              </p>
            )}
          </div>

          <span
            ref={btnRef}
            className="relative inline-flex items-center gap-1 overflow-hidden rounded-full px-3.5 py-1.5 text-[13px] font-medium text-ink transition-colors duration-200 group-hover:text-[#C9A84C]"
            onClick={handleRipple}
            aria-hidden="true"
          >
            {ripple && (
              <span
                className="pointer-events-none absolute rounded-full bg-[#C9A84C]/20"
                style={{
                  left: ripple.x - 30,
                  top:  ripple.y - 30,
                  width: 60,
                  height: 60,
                  animation: 'bundle-ripple 0.6s ease-out',
                }}
              />
            )}
            {bundle.cta_text ?? 'View Bundle'}
            <svg
              className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5"
              fill="none"
              viewBox="0 0 14 14"
              aria-hidden="true"
            >
              <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      </div>

      <style>{`
        @keyframes bundle-ripple {
          from { transform: scale(0); opacity: 1; }
          to   { transform: scale(4); opacity: 0; }
        }
      `}</style>
    </Link>
  );
}
